import React, { useContext, useState, useEffect } from 'react';
import {
  Button,
  Box,
  Typography,
  TextField,
  Collapse,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
} from '@mui/material';

import { useNextProjectStep } from '../../../hooks/useNextProjectStep'; // ✅ Make sure path is correct
import { usePreviousProjectStep } from '../../../hooks/usePreviousProjectStep';
import { ProjectContext } from '../../../layouts/CreateProjectLayout';
import axios from 'axios';

const TrainingDataStep = () => {

  const { project, setProject, canEditProject } = useContext(ProjectContext);
  const { goToNextStep } = useNextProjectStep(); 
  const { goToPreviousStep } = usePreviousProjectStep(); 

  const [dataSource, setDataSource] = useState('cvat');
  const [cvatMode, setCvatMode] = useState('new_project');
  const [selectedCompanies, setSelectedCompanies] = useState([]);
  const [selectedParts, setSelectedParts] = useState([]);
  const [selectedDefects, setSelectedDefects] = useState([]);
  const [cvatProjectID, setCvatProjectID] = useState(
    project?.cvat_id ? String(project.cvat_id) : ''
  );

  const companyOptions = ['Reynears', 'OptimaT', 'Alstom'];
  const partOptions = ['Profile', 'Fender', 'Bumper', 'Train'];
  const defectOptions = ['Crack', 'Dent', 'Orange Peel', 'Scratch'];

  useEffect(() => {
    if (project?.cvat_id) {
      setCvatProjectID(String(project.cvat_id));
      setCvatMode('project_id');
    }
  }, [project?.cvat_id]);

  const updateCvatProjectID = async (id) => {
    try {
      const updatedCvatUrl = project.cvat_url
        ? project.cvat_url.replace(/\/projects\/\d+/, `/projects/${id}`)
        : '';

      const response = await axios.patch(`/projects/${project.id}`, {
        cvat_id: id,
        cvat_url: updatedCvatUrl,
      });
      console.log('CVAT project ID updated:', response.data);

      setProject(prev => ({
        ...prev,
        cvat_id: id,
        cvat_url: updatedCvatUrl,
      }));

    } catch (error) {
      console.error(error);
      alert('Failed to update CVAT project ID');
    }
  };



  const handleOpenCVAT = () => {
    if (project?.cvat_url) {
      window.open(project.cvat_url, '_blank', 'noopener,noreferrer');
    } else {
      alert('CVAT URL not available');
    }
  };

  return (
    <Box
      sx={{
        mx: 'auto',
        mt: 1,
        borderRadius: 4,
        overflow: 'hidden',
        width: { xs: '80%', sm: '70%' },
        display: 'flex',
        flexDirection: 'column',
        p: 1,
      }}
    >
      <Typography variant="h3" fontWeight={700} color="black" mt={4}>
        Training Data
      </Typography>
      <Typography variant="h6" color="rgb(158, 151, 151)" mt={2}>
        Select your training data source. 
      </Typography>

      <Box
        sx={{
          mt: 3,
          width: '100%',
          maxWidth: 700,
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          p: 2,
        }}
      >
        <Typography variant="h7" fontWeight={500} color="black">
          Data source: 
        </Typography>

        <RadioGroup
          value={dataSource}
          onChange={(event) => setDataSource(event.target.value)}
          row
          sx={{ ml: 2 }}
          disabled={!canEditProject}
        >
          <FormControlLabel value="cvat" control={<Radio />} label="CVAT" disabled={!canEditProject} />
          {/* <FormControlLabel value="metadbase" control={<Radio />} label="MetaDBASE" /> */}
        </RadioGroup>
      </Box>

      <Collapse in={dataSource === 'cvat'} timeout="auto" unmountOnExit>
        <Box
          sx={{
            width: '100%',
            maxWidth: 700,
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            p:2, 
          }}
        >
          <Typography variant="h7" fontWeight={500} color="black">
            CVAT options ("Test" subsets will not be included in the training): 
          </Typography>

          <RadioGroup
            value={cvatMode}
            onChange={(event) => setCvatMode(event.target.value)}
            row
            sx={{ ml: 2 }}
            disabled={!canEditProject}
          >
            <FormControlLabel value="new_project" control={<Radio />} label="New project" disabled={!canEditProject} />
            <FormControlLabel value="project_id" control={<Radio />} label="Project ID" disabled={!canEditProject} />
          </RadioGroup>

          <Collapse in={cvatMode === 'new_project'} timeout="auto" unmountOnExit>
            <Box
              sx={{
                width: '100%',
                height: 150,
                border: '2px dashed #00707c',
                borderRadius: 2,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                cursor: 'pointer',
                color: '#00707c',
                userSelect: 'none',
                textAlign: 'center',
                px: 2,
                mt: 1,
              }}
              onClick={handleOpenCVAT}
            >
              <Typography fontWeight={700} variant="h6" mb={1}>
                Browse to access CVAT !
              </Typography>
            </Box>
          </Collapse>

          <Collapse
            in={cvatMode === 'project_id'}
            timeout="auto"
            unmountOnExit
            sx={{ width: '100%' }}
          >
            <Collapse
              in={cvatMode === 'project_id'}
              timeout="auto"
              unmountOnExit
              sx={{ width: '100%' }}
            >
        <Box
          sx={{
            mt: 1,
            width: '100%',
            display: 'flex',
            gap: 2,
            alignItems: 'flex-start'
          }}
        >
          
      <TextField
        fullWidth
        label="Enter your project ID"
        placeholder="Enter your project ID"
        variant="outlined"
        type="number"
        value={cvatProjectID}
        onChange={(e) => setCvatProjectID(e.target.value)}
        disabled={!canEditProject}
        helperText={
          project?.cvat_id
            ? `Current project ID: ${project.cvat_id}`
            : 'You can find this in the CVAT project URL'
        }
        sx={{
          flex: 1,
          '& .MuiOutlinedInput-root': { borderRadius: '12px' },
        }}
        inputProps={{
          inputMode: 'numeric',
          pattern: '[0-9]*',
        }}
      />


        <Button
          variant="contained"
          sx={{
            height: '56px',
            borderRadius: '12px',
            backgroundColor: '#16a4b0',
            textTransform: 'none',
            whiteSpace: 'nowrap',
            '&:hover': { backgroundColor: '#00707c' },
          }}
          disabled={!canEditProject}
          onClick={() => {updateCvatProjectID(cvatProjectID);}}
        >
          Save
        </Button>
                </Box>
              </Collapse>
          </Collapse>
        </Box>
      </Collapse>

      <Collapse in={dataSource === 'metadbase'} timeout="auto" unmountOnExit>
        <Box
          sx={{
            width: '100%',
            maxWidth: 700,
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            p: 2,
          }}
        >
          <Typography variant="h7" fontWeight={500} color="black">
            MetaDBASE options
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxWidth: '53.5%' }}>
            <FormControl fullWidth>
              <InputLabel id="company-select-label">Company</InputLabel>
              <Select
                labelId="company-select-label"
                multiple
                value={selectedCompanies}
                onChange={(event) => setSelectedCompanies(event.target.value)}
                label="Company"
                renderValue={(selected) => selected.join(', ')}
                sx={{ borderRadius: '12px' }}
              >
                {companyOptions.map((company) => (
                  <MenuItem key={company} value={company}>
                    <Checkbox checked={selectedCompanies.indexOf(company) > -1} />
                    <ListItemText primary={company} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel id="parts-select-label">Parts</InputLabel>
              <Select
                labelId="parts-select-label"
                multiple
                value={selectedParts}
                onChange={(event) => setSelectedParts(event.target.value)}
                label="Parts"
                renderValue={(selected) => selected.join(', ')}
                sx={{ borderRadius: '12px' }}
              >
                {partOptions.map((part) => (
                  <MenuItem key={part} value={part}>
                    <Checkbox checked={selectedParts.indexOf(part) > -1} />
                    <ListItemText primary={part} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel id="defects-select-label">Defects</InputLabel>
              <Select
                labelId="defects-select-label"
                multiple
                value={selectedDefects}
                onChange={(event) => setSelectedDefects(event.target.value)}
                label="Defects"
                renderValue={(selected) => selected.join(', ')}
                sx={{ borderRadius: '12px' }}
              >
                {defectOptions.map((defect) => (
                  <MenuItem key={defect} value={defect}>
                    <Checkbox checked={selectedDefects.indexOf(defect) > -1} />
                    <ListItemText primary={defect} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Box>
      </Collapse>

      <Box sx={{ padding: 2, width: '50%',  display: 'flex', pt: 2, justifyContent: 'center', gap: 4, mt: 4 }}>
          <Button
            variant="outlined"
            onClick={goToPreviousStep}
            sx={{
              borderRadius: '12px',
              textTransform: 'none',
              fontSize: '16px',
              width: '120px',
              color: '#16a4b0',
              borderColor: '#16a4b0',
              '&:hover': { backgroundColor: '#f0f0f0', borderColor: '#16a4b0' },
            }}
          >
            Previous
          </Button>

        <Button
          variant="contained"
          onClick={goToNextStep}
          sx={{
            borderRadius: '12px',
            backgroundColor: '#16a4b0',
            color: 'white',
            textTransform: 'none',
            fontSize: '16px',
            width: '120px',
            '&:hover': { backgroundColor: '#00707c' },
          }}
        >
          Next
        </Button>
      </Box>
    </Box>
  );
};

export default TrainingDataStep;
